import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ProjectCard from '../../components/ui/ProjectCard';
import ProjectDetailModal from '../../components/ui/ProjectDetailModal';
import { useProjectsQuery } from '../../hooks/useProjectQuery';
import { getActiveCategories, Category } from '../../firebase/categoryService';
import { Project } from '../../firebase/projectService';
import { usePageTitle } from '../../hooks/usePageTitle';
import { normalizeProjectLinks } from '../../utils/projectLinks';

const ProjectsPage: React.FC = () => {
  usePageTitle("Projects");
  
  const [filter, setFilter] = useState<string>('all');
  const [dynamicCategories, setDynamicCategories] = useState<Category[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const { data: projects = [], isLoading, error, refetch } = useProjectsQuery();

  // Load dynamic categories from the category service
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const categories = await getActiveCategories();
        setDynamicCategories(categories);
      } catch (err) {
        console.error('Error loading categories:', err);
      } finally {
        setCategoriesLoading(false);
      }
    };

    loadCategories();
  }, []);
  
  const filteredProjects = filter === 'all' 
    ? projects 
    : projects.filter(project => {
        // Check if filter matches any category in the new categories array
        if (project.categories && Array.isArray(project.categories)) {
          if (project.categories.includes(filter)) return true;
        }
        // Check legacy category field for backward compatibility
        if (project.category === filter) return true;
        // Check tags
        if (project.tags.includes(filter)) return true;
        return false;
      });

  // Dynamically generate filter buttons based on dynamic categories and project data
  const getUniqueFilterOptions = () => {
    // Start with dynamic categories from the category service (these have proper ordering and colors)
    const categoryOptions = dynamicCategories.map(category => ({
      label: category.name,
      value: category.name,
      color: category.color,
      type: 'category' as const
    }));

    return [
      { label: 'All', value: 'all', color: '#10B981', type: 'all' as const },
      ...categoryOptions
    ];
  };

  const filterButtons = getUniqueFilterOptions();

  return (
    <div className="container py-16">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="text-center mb-12">
          <span className="inline-block mb-4 px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider text-primary-700 dark:text-primary-300 bg-primary-50 dark:bg-primary-900/25 ring-1 ring-primary-200/60 dark:ring-primary-800/60">
            Portfolio
          </span>
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight bg-gradient-to-r from-primary-600 via-primary-500 to-purple-600 bg-clip-text text-transparent pb-1">
            My Projects
          </h1>
          <p className="text-gray-600 dark:text-gray-400 text-center max-w-2xl mx-auto mt-4">
            Explore my portfolio of full-stack applications. Each project demonstrates my expertise
            across multiple technologies — from backend APIs to frontend interfaces.
            Use the filters below to discover projects by category or technology.
          </p>
          {!isLoading && projects.length > 0 && (
            <div className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-gray-500 dark:text-gray-400">
              <span className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
              {filteredProjects.length} {filteredProjects.length === 1 ? 'project' : 'projects'}
              {filter !== 'all' && <span className="text-gray-400 dark:text-gray-500">in “{filter}”</span>}
            </div>
          )}
        </div>

        {error && (
          <div className="mb-8 p-4 bg-red-100 border border-red-400 text-red-700 rounded-md text-center">
            {error instanceof Error ? error.message : 'An error occurred while loading projects'}
            <button 
              onClick={() => refetch()}
              className="ml-2 underline"
            >
              Try Again
            </button>
          </div>
        )}

        {/* Filter buttons */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categoriesLoading ? (
            <div className="flex items-center text-gray-500 dark:text-gray-400">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600 mr-2"></div>
              Loading categories...
            </div>
          ) : (
            filterButtons.map((button) => (
              <button
                key={button.value}
                onClick={() => setFilter(button.value)}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all transform hover:scale-105 flex items-center ${
                  filter === button.value
                    ? 'bg-primary-600 text-white shadow-lg'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {button.color && button.type !== 'all' && (
                  <div
                    className="w-3 h-3 rounded-full mr-2"
                    style={{ backgroundColor: button.color }}
                  ></div>
                )}
                {button.label}
              </button>
            ))
          )}
        </div>

        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        ) : (
          <>
            {/* Projects grid */}
            <AnimatePresence>
              {projects.length === 0 ? (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-center py-12"
                >
                  <p className="text-xl text-gray-600 dark:text-gray-400">
                    No projects available yet. Check back soon!
                  </p>
                </motion.div>
              ) : (
                <motion.div 
                  className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
                  layout
                >
                  {filteredProjects.map((project) => (
                    <motion.div
                      key={project.id}
                      layout
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.5 }}
                      onClick={() => setSelectedProject(project)}
                      className="cursor-pointer h-full"
                    >
                      <ProjectCard
                        title={project.title}
                        description={project.description}
                        imageUrl={project.image}
                        tags={project.tags}
                        platforms={normalizeProjectLinks(project).map(l => l.type)}
                      />
                    </motion.div>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>

            {filteredProjects.length === 0 && projects.length > 0 && (
              <p className="text-center text-gray-600 dark:text-gray-400 mt-8">
                No projects found with the selected filter.
              </p>
            )}
          </>
        )}
      </motion.div>

      {/* Project Detail Modal — shared component for consistent UI across the site */}
      <ProjectDetailModal project={selectedProject} onClose={() => setSelectedProject(null)} />
    </div>
  );
};

export default ProjectsPage; 